import { HttpHeaders } from '@angular/common/http';
/* Settings Service */
/* @author Mahalingam Ganesan */
/* @Filename settings.service.ts */
/*
    Handles the operations related to the user settings data management.
    This is different from the user preferences which is typically user's locale preferences.
    Usecases:
    1. Retrieve the user preferences from a server
    2. Update the user preferences to server
    3. Persist the data in the client
    3. Clean up the data in the client
    The initial version will only deal with UC 1 & 2.
*/
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import { SettingsServiceDefinition } from '../service-definitions/settings-service-definition';
import { SettingsDetailServiceDefinition } from '../service-definitions/settings-detail-service-definition';
import { PershingPreferenceUtil } from '../utils/pershing-preference.util';
import { ISettingsService } from '../interfaces/settings.abstract.service';
import { SettingsDetails } from '../models/settings.model';
import { LogService } from './log.service';
import { ServiceRouter } from '../utils/service-router';
@Injectable()
export class UserSettingsService implements ISettingsService {
    static readonly SAVE_ACTION = 'saveUserSettings';
    static readonly UPDATE_ACTION = 'updateUserSettings';
    _settingsList: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    settingsList$: Observable<any> = this._settingsList.asObservable();
    _settingsDetails: ReplaySubject<SettingsDetails> = new ReplaySubject<SettingsDetails>(null);
    settingsDetails$: Observable<any> = this._settingsDetails.asObservable();
    _settingsSaveStatus: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    settingsSaveStatus$: Observable<any> = this._settingsSaveStatus.asObservable();
    public settingsServiceUrl: string;
    public settingsDetailsServiceUrl: string;
    constructor(private http: HttpClient, private logService: LogService, private serviceRouter: ServiceRouter) {
        logService.info('SettingsService : constructor Initializing');
        this.settingsServiceUrl = this.serviceRouter.getServiceRouteURL(SettingsServiceDefinition.getInstance(),
                                                                        { ignoreOptions: { constructFor: 'NDS' } });
        this.settingsDetailsServiceUrl = this.serviceRouter.getServiceRouteURL(SettingsDetailServiceDefinition.getInstance(),
                    { ignoreOptions: { constructFor: 'NDS' } });
    }
    /*
        Fetches the summary of all the saved user settings from server.
    */
    fetchUserSettingsSummary(): any {
        this.logService.info('SettingsService : Fetching the user preferences');
        let settingsList: any[] = null; /* To store the Settings */
        const header = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });
        // header= header.append('content-type', 'application/x-www-form-urlencoded');
        this.http.post(this.settingsServiceUrl, null, { headers: header })
            .subscribe((data: any) => {
                this.logService.info('SettingsService : PreferencesResponse :', data);
                if (typeof(data) !== 'undefined' && typeof(data.returnCode) !== 'undefined' &&
                    data.returnCode === 0 ) {
                    settingsList = data.userSettingsList;
                    this.logService.info('SettingsService : Fetching user settings summary SUCCESS - Retrieved summary :', settingsList);
                    this._settingsList.next(settingsList);
                } else {
                    /* Set the settingsList to NULL as the method may be called multiple times */
                    settingsList = null;
                    this.logService.error(`ERROR: SettingsService: Could not fetch the user settings summary. Error code: ${data['returnCode']}`);
                    this._settingsList.next(settingsList);
                }
            },
            error => {
                settingsList = null;
                this.logService.error('ERROR: SettingsService : ', error);
                this.logService.info('SettingsService : Retrieved summary :', settingsList);
                this._settingsList.next(settingsList);
            });
    }
    /*
        Fetches the details for a specific user settings from server.
    */
    fetchUserSettingsDetails(functionName: string, functionType: string): void {
        this.logService.info('SettingsService : Fetching the user preferences');
        let settingsDetails = { taskName: null, functionType: null,
            functionName: null, uniqueId: null, settings: null, updatedTimeStamp: null, dataSize: null,
            isAvailable: false} as SettingsDetails;
        let settingDetailsURL: string;
        settingDetailsURL = this.settingsDetailsServiceUrl + '?functionName=' + functionName + '&functionType=' + functionType;
        const header = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded'
        });
        this.http.post(settingDetailsURL, null, { headers: header })
            .subscribe((data: any) => {
                this.logService.info('SettingsService : SettingsDetail Response:', data);
                if (typeof data !== 'undefined' && typeof data.returnCode !== 'undefined' &&
                    data.returnCode === 0 &&
                    data.userSettingsList.length > 0) {
                        settingsDetails.functionType = data.userSettingsList[0].functionType;
                        settingsDetails.functionName = data.userSettingsList[0].functionName;
                        settingsDetails.uniqueId = data.userSettingsList[0].uniqueId;
                        settingsDetails.settings = JSON.parse(data.userSettingsList[0].settings);
                        settingsDetails.isAvailable = true;
                        this.logService.info('SettingsService : Fetching user settings SUCCESS');
                } else {
                    /* Set the SettingsDetails to NULL as the method may be called multiple times */
                    settingsDetails.functionName = functionName;
                    settingsDetails.functionType = functionType;
                    settingsDetails.isAvailable = false;
                    this.logService.error(`ERROR: SettingsService: Could not fetch the user settings. Error code: ${data['returnCode']}`);
                }
                this.logService.info('SettingsService : Retrieved ', settingsDetails);
                this._settingsDetails.next(settingsDetails as SettingsDetails);
            },
            error => {
                settingsDetails.functionName = functionName;
                settingsDetails.functionType = functionType;
                settingsDetails.isAvailable = false;
                this._settingsDetails.next(settingsDetails as SettingsDetails);
                this.logService.error('ERROR: SettingsService : Could not fetch the user settings. ', error);
            });
    }
    saveUserSettings(settingsData: any): any {
        if (settingsData.uniqueId && settingsData.uniqueId.length > 0 ) {
            this.logService.info('SettingsService : Updating existing setting : ', settingsData);
            this.modifyUserSettings(settingsData, UserSettingsService.UPDATE_ACTION);
        } else {
            this.logService.info('SettingsService : Saving new setting - calling modifyUserSettings: ', settingsData);
            this.modifyUserSettings(settingsData, UserSettingsService.SAVE_ACTION);
        }
    }
    /*
        Saves user preferences from server.
        Throws error with appropriate details on failure and resets the Settings on failure.
    */
    modifyUserSettings(settingsData: SettingsDetails, action: string, isRetry?: boolean): any {
        const settingsValueString: string = JSON.stringify(settingsData.settings);
        const uniqueIdValue: string = settingsData.uniqueId ? settingsData.uniqueId : null;
        let dbSettingsData: any;
        dbSettingsData = {
            isForAllSite : settingsData.isForAllSite,
            taskName: action,
            functionName: settingsData.functionName,
            functionType: settingsData.functionType,
            settings: settingsValueString,
            updatedTimeStamp: PershingPreferenceUtil.getCurrentTimestamp(),
            dataSize: settingsValueString.length,
            uniqueId: uniqueIdValue
        };
        this.logService.info('SettingsService : Settings Data to save :', dbSettingsData);
        this.http.post(this.settingsServiceUrl, dbSettingsData, { responseType: 'json' })
            .subscribe((data: any) => {
                if (data.returnCode === 0) {
                    this._settingsSaveStatus.next(true);
                    this.logService.info('SettingsService : Saving user settings SUCCESS', data);
                    // publishing first time saved details to the consumer
                    if (action === UserSettingsService.SAVE_ACTION && data.uniqueId != null && data.uniqueId.trim() !== '') {
                        settingsData.uniqueId = data.uniqueId;
                        settingsData.isAvailable = true;
                        settingsData.isSaveNotification = true;
                        this.logService.info('SettingsService : Publishing saved details to feature during initial save');
                        this._settingsDetails.next(settingsData as SettingsDetails);
                    }
                } else {
                    if (isRetry) {
                        this.logService.error('ERROR: SettingsService - While retrying save for data :', data);
                        this._settingsSaveStatus.next(false);
                    } else {
                        /* One retry if SAVE call failed */
                        this.logService.error('ERROR: SettingsService - While saving. Retrying ...');
                        this.modifyUserSettings(settingsData, action, true);
                    }
                }
            },
            error => {
                if (isRetry) {
                    this.logService.error('ERROR: SettingsService - While retrying save for data, HTTP error :', error);
                    this._settingsSaveStatus.next(false);
                } else {
                    /* One retry if SAVE call failed */
                    this.logService.error('ERROR: SettingsService - While saving. Retrying ...');
                    this.modifyUserSettings(settingsData, action, true);
                }
            });
    }
}
